/**
 * This file contains all the routes used in the application.
 * It is used to avoid hardcoding the routes in the application.
 * Update the routes here and use them in the application.
 * When changing the routes or removing the routes, make sure to add redirects for fallback routes.
 */

/**
 * Auth routes for the application. i.e. Sign In, Sign Up, Reset Password, etc.
 */
export const AuthRoutes = {
  SignIn: '/sign-in',
  SignUp: '/sign-up',
  SignUpOnboarding: '/sign-up/onboarding',
  SignUpAddPaymentMethod: '/sign-up/add-payment-method',
  SignUpWorkerOnboarding: '/sign-up/worker-onboarding',
  SignUpNeedsBooking: '/sign-up/book-meeting',
  ResetPassword: '/reset-password',
  Authenticate: '/authenticate',
  Logout: '/logout',
  EmailVerification: '/verify-email',
  SuperAdmin: '/super-admin',
  ActionOnQuoteForCustomerRoute: '/ap-ar/quote/[quoteID]',
  TheraStart: '/start',
  TheraStartSuccess: '/start/success',
  TheraStartCancel: '/start/cancel',
  TheraWhiteLabelling: '/thera-white-labelling',
  TheraComponents: '/thera-components',
  SecureUploadRequest: '/secure-upload/request',
};

/**
 * Pages anyone may open in any session state. No sign-in redirect, MFA challenge or account gate
 * applies, because the people who reach them can be signed in with an account problem.
 */
const ALWAYS_PUBLIC_ROUTES: string[] = [AuthRoutes.TheraWhiteLabelling, AuthRoutes.SecureUploadRequest];

export const isAlwaysPublicRoute = (pathname: string): boolean => ALWAYS_PUBLIC_ROUTES.includes(pathname);

/**
 * Admin routes for the application.
 * These routes are only accessible to the admin users.
 */
export type ManagerAccessType =
  | 'w2Admin'
  | 'hasEOREmployees'
  | 'isUSCompany'
  | 'isAREnabled'
  | 'isAPEnabled'
  | 'canEditAR'
  | 'canEditAP'
  | 'canEditBanking'
  | 'isPayrollEnabled'
  | 'managerWithTeams'
  | 'orgAdmin'
  | 'onlyOrgAdmin'
  | 'companyWithEorTracking'
  | 'isCompanyImplemented'
  | 'isImplementationAndOnBoardingComplete'
  | 'isPayrollRunnable'
  | 'isInSuperAdminMode'
  | 'canShowManagers'
  | 'isBankingEnabled'
  | 'canShowNotifications'
  | 'timeTracking'
  | 'canUserCreateBridgeWallet'
  | 'isCopilotEnabled'
  | 'isTheraDemoCompany'
  | 'isTheraProdCompany'
  | 'isWhiteLabelClientAdmin'
  | 'isOrgChartEnabled'
  | 'isCorridorRatesEnabled'
  | 'isPayoutVolumeEnabled'
  | 'isAchExposureEnabled'
  | null;

export type WorkerAccessType =
  | 'contractor'
  | 'eorEmployee'
  | 'w2Employee'
  | 'w2EmployeeWithContract'
  | 'isKycAndComplianceEnabled'
  | 'isTheraAdvanceEligible'
  | 'isTheraAdvanceCompany'
  | 'isFastPaymentsDisabled';

export interface AccessConditions {
  readonly anyOf?: readonly (ManagerAccessType | WorkerAccessType)[];
  readonly allOf?: readonly (ManagerAccessType | WorkerAccessType)[];
}
export interface IRouteInfoAdmin {
  readonly route: string;
  readonly access?: AccessConditions | readonly ManagerAccessType[];
  readonly helperLabel?: string;
}
export interface IRouteInfoWorker {
  readonly route: string;
  readonly access?: AccessConditions | readonly WorkerAccessType[];
  readonly helperLabel?: string;
}

export const AdminRoutesWithAccess = {
  // Common
  Dashboard: { route: '/home' },
  SecureUpload: { route: '/secure-upload' },
  Calendly: { route: '/calendly' },
  Finance: { route: '/finance' },
  CompanyContracts: { route: '/company-contracts' },
  PendingSignatures: {
    route: '/company-contracts/pending-signatures',
    helperLabel: 'Contracts Pending Your Signature',
  },
  // Analytics Routes
  Analytics: { route: '/analytics', helperLabel: 'Analytics Dashboard', access: ['isTheraProdCompany'] },
  AnalyticsOverview: {
    route: '/analytics/overview',
    helperLabel: 'Analytics Overview',
    access: ['isTheraProdCompany'],
  },
  AnalyticsRevenue: { route: '/analytics/revenue', helperLabel: 'Revenue Analytics', access: ['isTheraProdCompany'] },
  AnalyticsExpenses: {
    route: '/analytics/expenses',
    helperLabel: 'Operating Expenses Analytics',
    access: ['isTheraProdCompany'],
  },
  AnalyticsPayroll: { route: '/analytics/payroll', helperLabel: 'Payroll Analysis', access: ['isTheraProdCompany'] },
  Apps: { route: '/apps', helperLabel: 'Apps', access: ['orgAdmin'] },
  AI: { route: '/copilot', helperLabel: 'Analyst', access: ['isCopilotEnabled', 'orgAdmin'] },
  // Settings Routes
  AccountPreferences: { route: '/account-preferences', helperLabel: 'General Settings' },
  ProfileSettings: { route: '/account-preferences/profile', helperLabel: 'Profile' },
  SecuritySettings: { route: '/account-preferences/security', helperLabel: 'Security' },
  Teams: { route: '/account-preferences/teams-settings', helperLabel: 'Teams' },
  PaymentMethods: { route: '/account-preferences/payments', helperLabel: 'Payment Methods' },
  WorkersCompCompliance: {
    route: '/payroll/workers-comp',
    helperLabel: 'Workers Compensation, Compliance',
    // Confirming or buying coverage is pointless once Check has closed or held the account.
    access: ['w2Admin', 'isPayrollRunnable'],
  },
  APPaymentMethods: {
    route: '/account-preferences/ap/payments',
    helperLabel: 'AP Payment Methods',
    access: ['isAPEnabled'],
  },
  ARReceivingMethods: {
    route: '/account-preferences/ar/receiving-methods',
    helperLabel: 'AR Receiving Methods',
    access: ['isAREnabled'],
  },
  Billing: { route: '/account-preferences?selectedTab=billing' },
  Notifications: {
    route: '/account-preferences/notifications',
    helperLabel: 'Notifications',
    access: ['canShowNotifications'],
  },
  CorridorRates: {
    route: '/account-preferences/corridor-rates',
    helperLabel: 'Corridor Rates',
    access: ['isCorridorRatesEnabled'],
  },
  PayoutVolume: {
    route: '/account-preferences/payout-volume',
    helperLabel: 'Payout Volume',
    access: ['isPayoutVolumeEnabled'],
  },
  AchExposure: {
    route: '/account-preferences/ach-exposure',
    helperLabel: 'ACH Exposure',
    access: ['isAchExposureEnabled'],
  },
  // orgAdmin
  Roles: { route: '/account-preferences/roles', access: ['canShowManagers'], helperLabel: 'Roles, Managers, Admins' },
  ApiKeys: { route: '/account-preferences/api-keys', access: ['onlyOrgAdmin'], helperLabel: 'API Keys' },
  ActivityLog: {
    route: '/account-preferences/activity-log',
    access: ['orgAdmin'],
    helperLabel: 'Activity Log, Audit Trail, Security',
  },
  // Payroll Routes
  PayrollDashboard: { route: '/payroll', helperLabel: 'Payroll Dashboard', access: ['isPayrollEnabled'] },
  Team: { route: '/team', helperLabel: 'Team, Employees, Contractors', access: ['isPayrollEnabled'] },
  TeamOrgChart: {
    route: '/team/org-chart',
    helperLabel: 'Org Chart',
    access: ['isPayrollEnabled', 'isOrgChartEnabled'],
  },
  AddEmployee: { route: '/team/employee/add', helperLabel: 'Add US Employee', access: ['isPayrollEnabled'] },
  AddContractor: { route: '/team/contractor/add', helperLabel: 'Add New Contractor', access: ['isPayrollEnabled'] },
  AddEOR: { route: '/team/eor-employee/add', helperLabel: 'Add EOR Employee', access: ['isPayrollEnabled'] },
  ContractorProfile: { route: '/team/contractor' },
  ContractorProfileByID: { route: '/team/contractor/[userID]' },
  ContractorTimeOff: { route: '/team/contractor/[userID]/time-off' },
  ContractorContractPage: { route: '/team/contractor/[userID]/contracts/[contractID]' },
  EditContract: { route: '/team/contractor/[userID]/contracts/[contractID]/edit-contract' },
  EndContract: { route: '/team/contractor/[userID]/contracts/[contractID]/end-contract' },
  Payments: { route: '/payments', helperLabel: 'Payments and History', access: ['isPayrollEnabled'] },
  Fees: { route: '/payments/fees', helperLabel: 'Fees', access: ['onlyOrgAdmin', 'isPayrollEnabled'] },
  FeesMonth: { route: '/payments/fees/[month]', access: ['onlyOrgAdmin', 'isPayrollEnabled'] },
  InvoicesPaymentReceipt: { route: '/payments/invoices/[receiptID]' },
  EORPaymentReceipt: { route: '/payments/eor/[receiptID]' },
  W2SubscriptionPaymentReceipt: { route: '/payments/w2subscription/[receiptID]' },
  USPayrollPaymentReceipt: { route: '/payments/us-payroll/[payrollID]' },
  Invoices: { route: '/admin-invoice', helperLabel: 'Invoices History', access: ['isPayrollEnabled'] },
  TimeOff: { route: '/time-off', helperLabel: 'Time Off', access: ['isPayrollEnabled'] },
  TimeOffBalances: { route: '/time-off/balances', helperLabel: 'Balances' },
  ReviewTimeOffs: { route: '/time-off/review', helperLabel: 'Review Time Off' },
  TimeTrackingOverview: {
    route: '/time-tracking',
    helperLabel: 'Time Tracking',
    access: ['w2Admin'],
  },
  ReviewTimesheets: {
    route: '/time-tracking/review',
    helperLabel: 'Review Timesheets',
    access: ['w2Admin'],
  },
  ReviewTimesheet: {
    route: '/time-tracking/review/[timesheetId]',
    helperLabel: 'Review Timesheet',
    access: ['w2Admin'],
  },
  TimeTrackingPayrollReview: {
    route: '/time-tracking/payroll-review',
    helperLabel: 'Payroll Timesheet Review',
    access: ['w2Admin'],
  },
  TimeTrackingCarryover: {
    route: '/time-tracking/carryover',
    helperLabel: 'Carryover Timesheets',
    access: ['w2Admin', 'orgAdmin'],
  },
  ReviewInvoices: { route: '/admin-invoice/review', helperLabel: 'Review Invoices' },
  PayApprovedInvoices: { route: '/admin-invoice/pay-approved', helperLabel: 'Pay Approved Invoices' },
  NotDueInvoices: { route: '/admin-invoice/not-due', helperLabel: 'Not Due Invoices' },
  AddTeam: { route: '/team/add', access: ['isPayrollEnabled'] },
  ManageTeam: { route: '/team/manage', access: ['isPayrollEnabled'] },
  Contractors: { route: '/contractors', helperLabel: 'Contractors, Recent Invoices', access: ['isPayrollEnabled'] },
  PayrollOnboarding: { route: '/payroll/us-payroll/onboarding', access: ['isPayrollEnabled'] },
  W2Integrations: {
    route: '/payroll/us-payroll/benefits',
    access: ['isCompanyImplemented', 'isPayrollEnabled'],
    helperLabel: 'US Benefits',
  },
  IntegrationTypes: {
    route: '/payroll/us-payroll/benefits/[integrationType]',
    access: ['isCompanyImplemented'],
  },
  NextWorkersIntegration: {
    route: '/payroll/us-payroll/benefits/nextworkers',
    access: ['isCompanyImplemented'],
  },
  ContractorSubscriptionPaymentReceipt: { route: '/payments/contractor-subscription/[receiptID]' },
  MiscPaymentReceipt: { route: '/payments/misc-payment/[receiptID]' },
  // EOR
  EOR: {
    route: '/eor',
    access: ['hasEOREmployees'],
    helperLabel: 'EOR, EOR Employees, EOR Help & Support',
  },
  EORPayrollHistory: { route: '/eor/payroll-history', access: ['hasEOREmployees'], helperLabel: 'EOR Payroll History' },
  EOREmployeeProfile: { route: '/team/eor-employee' },
  EOREmployeeProfileByID: { route: '/team/eor-employee/[userID]' },
  EndContractEOR: { route: '/team/eor-employee/[userID]/contracts/[contractID]/end-contract' },
  EOREmployeeEmployment: { route: '/team/eor-employee/[userID]/employments/[contractID]' },
  // W2 Routes
  EmployeeProfile: { route: '/team/employee' },
  EmployeeProfileByID: { route: '/team/employee/[employeeID]' },
  EmployeeTimeOff: { route: '/team/employee/[employeeID]/time-off' },
  TerminateEmployment: { route: '/team/employee/[employeeID]/terminate' },
  EditCompensation: { route: '/team/employee/[employeeID]/edit-pay' },
  PayrollHistory: {
    route: '/payroll/us-payroll/history',
    access: ['isCompanyImplemented'],
    helperLabel: 'Payroll History, Regular Payroll History',
  },
  RegularPayrollHistory: { route: '/payroll/us-payroll/history/[employerCheckID]', access: ['isCompanyImplemented'] },
  USPayroll: {
    route: '/payroll/us-payroll',
    // Not `w2Admin` — this is also the pre-onboarding landing page, which can't require
    // onboarding to already be complete; the page gates real dashboard content separately.
    access: { allOf: ['isUSCompany', 'isPayrollEnabled'] },
    helperLabel: 'Run Payroll, Deductions, Employee Checks',
  },
  AutopilotPayrollCancel: {
    route: '/payroll/us-payroll?cancel=true',
    access: ['w2Admin'],
  },
  USPayrollRun: {
    route: '/payroll/us-payroll/run',
    access: ['isImplementationAndOnBoardingComplete', 'isPayrollRunnable'],
    helperLabel: 'Run Payroll',
  },
  USPayrollRunOffCycle: {
    route: '/payroll/us-payroll/off-cycle',
    access: ['isImplementationAndOnBoardingComplete', 'isPayrollRunnable'],
    helperLabel: 'Run Off-Cycle Payroll',
  },
  USPayrollRunBonus: {
    route: '/payroll/us-payroll/bonus',
    access: ['isImplementationAndOnBoardingComplete', 'isPayrollRunnable'],
    helperLabel: 'Run Bonus Payroll',
  },
  USPayrollPaySchedules: {
    route: '/payroll/us-payroll/pay-cycles',
    access: ['isCompanyImplemented'],
    helperLabel: 'Pay Cycles',
  },
  USPayrollPaySchedulesDetails: {
    route: '/payroll/us-payroll/pay-cycles/[payScheduleID]',
    access: ['isCompanyImplemented'],
  },
  USPayrollPaySchedulesEmployees: {
    route: '/payroll/us-payroll/pay-cycles/employees',
    access: ['isCompanyImplemented'],
    helperLabel: 'Manage Employees Pay Cycles',
  },
  WorkLocations: {
    route: '/payroll/us-payroll/work-locations',
    access: ['isCompanyImplemented'],
    helperLabel: 'Work Locations',
  },
  EmployeeChecks: {
    route: '/payroll/us-payroll/employee-checks',
    access: ['isCompanyImplemented'],
    helperLabel: 'Employee Checks, Pending Checks',
  },
  PendingEmployeeCheck: {
    route: '/payroll/us-payroll/employee-checks/[employeeCheckID]',
    access: ['isCompanyImplemented'],
  },
  PayrollCompliance: {
    route: '/payroll/us-payroll/compliance',
    access: ['w2Admin'],
    helperLabel: 'Compliance, Tax Compliance, State Compliance',
  },
  Deductions: {
    route: '/payroll/us-payroll/benefits/employee',
    access: ['isCompanyImplemented'],
    helperLabel: 'Deductions, 401k, ira, hsa',
  },
  CompanyDeductions: {
    route: '/payroll/us-payroll/benefits/company',
    access: ['isCompanyImplemented'],
    helperLabel: 'Company Deductions, Company Benefits',
  },
  Garnishments: {
    route: '/payroll/us-payroll/benefits/child-support-garnishments',
    access: ['isCompanyImplemented'],
    helperLabel: 'garnishments, child support',
  },
  Earnings: {
    route: '/payroll/us-payroll/earnings',
    access: ['isCompanyImplemented'],
    helperLabel: 'Earnings, Reimbursements, Bonuses',
  },
  DeductionTypes: { route: '/payroll/us-payroll/benefits/employee/[deductionType]', access: ['isCompanyImplemented'] }, // Garnishment, Section 125, HSA, 401k
  EarningTypes: { route: '/payroll/us-payroll/earnings/[earningType]', access: ['isCompanyImplemented'] }, // Garnishment, Section 125, HSA, 401k
  Expenses: { route: '/payroll/expenses', access: ['isPayrollEnabled'], helperLabel: 'Expenses' },
  ReviewExpenses: { route: '/payroll/expenses/review', helperLabel: 'Review Expenses' },
  ZeroWageFilings: { route: '/taxes/zero-wage-filings', access: ['isCompanyImplemented'], helperLabel: 'Tax Setup' },
  RegisterNewState: { route: '/taxes/register-state', access: ['isCompanyImplemented'] },
  TaxDeposits: { route: '/taxes/deposits', access: ['isCompanyImplemented'], helperLabel: 'Tax Deposits' },
  Taxes: { route: '/taxes', access: ['isCompanyImplemented'], helperLabel: 'Taxes, W2, 1099' },
  FederalTaxes: { route: '/taxes/federal', access: ['w2Admin'] },
  StateTaxes: { route: '/taxes/[state]', access: ['w2Admin'] },
  // US Company Routes
  Documents: {
    route: '/documents',
    access: { allOf: ['isUSCompany', 'isPayrollEnabled'] },
    helperLabel: 'Documents, W8',
  },
  IRSFiling: {
    route: '/irs-filing',
    access: { allOf: ['isUSCompany', 'isPayrollEnabled'] },
    helperLabel: 'IRS Filings',
  },
  //
  BulkContractorUpload: { route: '/bulk-upload', access: ['isPayrollEnabled'] },
  WhiteLabelCompanies: {
    route: '/account-preferences/companies',
    access: ['isWhiteLabelClientAdmin'],
    helperLabel: 'Companies signed up under your white label',
  },
  TimeOffPolicy: {
    route: '/account-preferences/time-off-policies',
    helperLabel: 'Time Off Policy',
  },
  TimeTrackingSettings: {
    route: '/account-preferences/time-tracking-settings',
    helperLabel: 'Time Tracking Settings',
    // Time tracking is a US W-2 payroll feature, so gate it like the rest of US Payroll (w2Admin =
    // payroll enabled + W-2 admin + onboarding complete) - both the nav link and direct URL access.
    // These settings only ever feed a payroll run, so a closed or held account cannot change them.
    // orgAdmin (Org Admin or org-level HR Specialist) keeps team-level HR and Team Admins out, since
    // the backend rejects them on this endpoint.
    access: ['w2Admin', 'isPayrollRunnable', 'orgAdmin'],
  },
  Integrations: {
    route: '/account-preferences/integrations',
    access: ['orgAdmin'],
    helperLabel: 'Manage Integrations - Quickbooks, Google Calendar',
  },
  QBIntegrationsUSPayroll: {
    route: '/account-preferences/integrations/quickbooks-us-payroll',
    access: ['isCompanyImplemented'],
    helperLabel: 'QuickBook for US payroll',
  },
  QBIntegrations: {
    route: '/account-preferences/integrations/quickbooks',
    access: ['orgAdmin'],
    helperLabel: 'QuickBooks for Contractor Management',
  },
  QBIntegrationsEdit: {
    route: '/account-preferences/integrations/quickbooks?edit=true',
    access: ['orgAdmin'],
  },
  QBIntegrationsConnect: {
    route: '/account-preferences/integrations/quickbooks/connect',
    access: ['orgAdmin'],
    helperLabel: 'QuickBook connect for Contractors',
  },
  GoogleCalendar: {
    route: '/account-preferences/integrations/google-calendar',
    access: ['orgAdmin'],
    helperLabel: 'Google Calendar',
  },
  TimeOffPolicyByID: {
    route: '/account-preferences/time-off-policies/[policyID]',
    helperLabel: 'Time Off Policy ID',
  },
  // AR (Accounts Receivable) - Invoicing
  AR: {
    route: '/ar',
    access: ['isAREnabled'],
    helperLabel: 'Accounts Receivable',
  },
  AP: {
    route: '/ap',
    access: ['isAPEnabled'],
    helperLabel: 'Accounts Payable',
  },
  AROnboarding: {
    route: '/ar/onboarding',
    access: ['canEditAR'],
    helperLabel: 'Thera Payments Onboarding',
  },
  APOnboarding: {
    route: '/ap/onboarding',
    access: ['canEditAP'],
    helperLabel: 'Thera Spend Onboarding',
  },
  ARInvoices: {
    route: '/ar/invoices',
    access: ['isAREnabled'],
    helperLabel: 'Invoices',
  },
  ARInvoiceTemplateCreation: {
    route: '/ar/invoices/create-template',
    access: ['canEditAR'],
    helperLabel: 'Create Payments Invoice Template',
  },
  ARInvoiceTemplateEdit: {
    route: '/ar/invoices/edit-template/[invoiceTemplateID]',
    access: ['canEditAR'],
    helperLabel: 'Edit Payments Invoice Template',
  },
  ARTransactions: {
    route: '/ar/transactions',
    access: ['isAREnabled'],
    helperLabel: 'Transactions',
  },
  ARContacts: {
    route: '/ar/contacts',
    access: ['isAREnabled'],
    helperLabel: 'AR Contacts',
  },
  APContacts: {
    route: '/ap/contacts',
    access: ['isAPEnabled'],
    helperLabel: 'AP Contacts',
  },
  APCards: {
    route: '/ap/cards',
    access: { allOf: ['isAPEnabled', 'isTheraProdCompany'] },
    helperLabel: 'Cards',
  },
  APCardDetails: {
    route: '/ap/cards/[cardId]',
    access: { allOf: ['isAPEnabled', 'isTheraProdCompany'] },
    helperLabel: 'Card Details',
  },
  APTransactions: {
    route: '/ap/transactions',
    access: ['isAPEnabled'],
    helperLabel: 'Transactions',
  },
  ARInvoiceCreation: {
    route: '/ar/invoices/create',
    access: ['canEditAR'],
    helperLabel: 'Create Invoices',
  },
  ARInvoiceEdit: {
    route: '/ar/invoices/[invoiceID]/edit',
    access: ['canEditAR'],
  },
  ARQuoteCreation: {
    route: '/ar/quote/create',
    access: ['canEditAR'],
    helperLabel: 'Create Quote',
  },
  APBillPay: {
    route: '/ap/bill-pay',
    access: ['isAPEnabled'],
    helperLabel: 'Bill Pay',
  },
  APBillCreate: {
    route: '/ap/bill-pay/create',
    access: ['canEditAP'],
    helperLabel: 'Create Bill',
  },
  APBillEdit: {
    route: '/ap/bill-pay/[payableID]/edit',
    access: ['canEditAP'],
  },
  APBillTemplateCreation: {
    route: '/ap/bill-pay/create-template',
    access: ['canEditAP'],
    helperLabel: 'Create Spend Invoice Template',
  },
  APBillTemplateEdit: {
    route: '/ap/bill-pay/edit-template/[invoiceTemplateID]',
    access: ['canEditAP'],
    helperLabel: 'Edit Spend Invoice Template',
  },
  InvoiceTemplates: {
    route: '/account-preferences/invoice-templates',
    access: ['canEditAP'],
    helperLabel: 'Invoice Templates',
  },
  InvoiceTemplatesPreview: {
    route: '/account-preferences/invoice-templates/[templateId]',
    access: ['canEditAP'],
  },
  //banking
  BankingOnboarding: {
    route: '/banking/onboarding',
    access: ['canEditBanking'],
  },
  BankingOverview: {
    route: '/banking',
    access: { allOf: ['isBankingEnabled', 'isUSCompany'] },
  },
  BankingTransactions: {
    route: '/banking/transactions',
    access: { allOf: ['isBankingEnabled', 'isUSCompany'] },
    helperLabel: 'Banking Transactions',
  },
  BankingTransactionDetails: {
    route: '/banking/transactions/[transactionID]',
    access: { allOf: ['isBankingEnabled', 'isUSCompany'] },
  },
  BankingAccounts: { route: '/banking/accounts', access: { allOf: ['isBankingEnabled', 'isUSCompany'] } },
  BankingRecipients: { route: '/banking/recipients', access: { allOf: ['isBankingEnabled', 'isUSCompany'] } },
  BankingTransfers: { route: '/banking/transfers', access: { allOf: ['canEditBanking', 'isUSCompany'] } },
  BankingAddFunds: { route: '/banking/add-funds', access: { allOf: ['canEditBanking', 'isUSCompany'] } },
  TPOnboarding: {
    route: '/additional-info',
  },
} as const satisfies Record<string, IRouteInfoAdmin>;

const AdminRoutes = {} as { -readonly [k in keyof typeof AdminRoutesWithAccess]: string };
for (const key in AdminRoutesWithAccess) {
  AdminRoutes[key as keyof typeof AdminRoutesWithAccess] =
    AdminRoutesWithAccess[key as keyof typeof AdminRoutesWithAccess].route;
}

/**
 * Worker routes for the application.
 * These routes are only accessible to the worker users.
 */

export const WorkerRoutesWithAccess = {
  // Common
  Dashboard: { route: '/home' },
  Apps: { route: '/apps' },
  // AI: { route: '/copilot' },
  Finance: { route: '/finance' },
  AccountPreferences: { route: '/account-preferences', helperLabel: 'General Settings' },
  SecuritySettings: { route: '/account-preferences/security', helperLabel: 'Security' },
  SecureUpload: { route: '/secure-upload' },
  // Contractor Routes
  InvoiceHistory: { route: '/invoice-history', access: ['contractor'], helperLabel: 'All Invoices' },
  TimeOff: { route: '/time-off-history', access: { anyOf: ['contractor', 'w2Employee'] }, helperLabel: 'Time Off' },
  TheraAdvance: {
    route: '/thera-advance',
    access: ['contractor', 'isTheraAdvanceEligible', 'isTheraAdvanceCompany', 'isFastPaymentsDisabled'],
    helperLabel: 'Thera Advance, Advance Salary',
  },
  TheraAdvanceTransaction: {
    route: '/thera-advance/[withdrawalID]',
    access: ['contractor', 'isTheraAdvanceEligible', 'isTheraAdvanceCompany', 'isFastPaymentsDisabled'],
  },
  Withdrawals: { route: '/withdrawals', access: ['contractor'], helperLabel: 'Withdrawals' },
  WithdrawalInfo: { route: '/withdrawals/[withdrawalID]', access: ['contractor'] },
  Wallet: { route: '/wallet', access: { allOf: ['contractor', 'canUserCreateBridgeWallet'] }, helperLabel: 'Wallet' },
  WalletWithdrawalInfo: {
    route: '/wallet/[transactionID]',
    access: { allOf: ['contractor'] },
  },
  WithdrawalMethods: { route: '/withdrawal-methods', access: ['contractor'], helperLabel: 'Manage Withdrawal Methods' }, //redirects to /withdrawals page
  ComplianceDocs: {
    route: '/compliance-documents',
    access: { allOf: ['contractor', 'isKycAndComplianceEnabled'] },
    helperLabel: 'Compliance Documents, ID Verification',
  },
  // EOR Routes
  EOREmployeePayslips: { route: '/payslips', access: ['eorEmployee'] },
  // EOR + Contractor
  Contracts: {
    route: '/contracts',
    access: { anyOf: ['contractor', 'eorEmployee', 'w2EmployeeWithContract'] },
    helperLabel: 'Employment Contracts',
  },
  ContractDetails: {
    route: '/contracts/[contractID]',
    access: { anyOf: ['contractor', 'eorEmployee', 'w2EmployeeWithContract'] },
  },
  ContractSign: {
    route: '/contracts/[contractID]/sign-contract',
    access: { anyOf: ['contractor', 'eorEmployee', 'w2EmployeeWithContract'] },
  },
  // W2 Routes
  EmployeeOnboarding: { route: '/employee-onboarding', access: ['w2Employee'] },
  EmployeePaystubs: { route: '/paystubs', access: ['w2Employee'], helperLabel: 'All Paystubs' },
  EmployeeWithholdings: { route: '/employee-withholdings', access: ['w2Employee'], helperLabel: 'All Withholdings' },
  EmployeeUpdateWithholdings: { route: '/employee-withholdings/update-withholdings', access: ['w2Employee'] },
  EmployeeBenefits: { route: '/benefits', access: ['w2Employee'] },
  EmployerProfile: { route: '/employer-profile/[companyID]', access: ['w2Employee'] },
  // time tracking
  TimeTracking: {
    route: '/time-tracking',
    access: { anyOf: ['contractor', 'w2Employee'], allOf: ['timeTracking'] },
  },
  // Timesheets are a W-2 payroll concept — contractor time tracking submits entries to invoices
  // instead, so these pages 404 for contractors and must not be offered to them.
  Timesheet: {
    route: '/timesheet',
    helperLabel: 'Timesheet',
    access: { anyOf: ['w2Employee'], allOf: ['timeTracking'] },
  },
  Timesheets: {
    route: '/timesheets',
    helperLabel: 'Timesheets',
    access: { anyOf: ['w2Employee'], allOf: ['timeTracking'] },
  },
  WalletOnboarding: { route: '/wallet-onboarding', access: { allOf: ['contractor', 'canUserCreateBridgeWallet'] } },
  // Payroll Expenses
  Expenses: { route: '/expenses', access: ['w2Employee'], helperLabel: 'Expenses' },
  // Bank Accounts
  EmployeeBankAccounts: { route: '/banks', access: ['w2Employee'], helperLabel: 'Bank Accounts' },
  EmployeeDocuments: { route: '/employee-documents', access: ['w2Employee'], helperLabel: 'Documents' },
} as const satisfies Record<string, IRouteInfoWorker>;

const ContractorRoutes = {} as { -readonly [k in keyof typeof WorkerRoutesWithAccess]: string };
for (const key in WorkerRoutesWithAccess) {
  ContractorRoutes[key as keyof typeof WorkerRoutesWithAccess] =
    WorkerRoutesWithAccess[key as keyof typeof WorkerRoutesWithAccess].route;
}

export { AdminRoutes, ContractorRoutes };

// Pre-computed pathname-keyed maps for O(1) route lookups
export const adminRoutesByPath: Record<string, IRouteInfoAdmin> = {};
for (const key in AdminRoutesWithAccess) {
  const info = AdminRoutesWithAccess[key as keyof typeof AdminRoutesWithAccess];
  adminRoutesByPath[info.route] = info;
}

export const workerRoutesByPath: Record<string, IRouteInfoWorker> = {};
for (const key in WorkerRoutesWithAccess) {
  const info = WorkerRoutesWithAccess[key as keyof typeof WorkerRoutesWithAccess];
  workerRoutesByPath[info.route] = info;
}

/**
 * Referral portal routes — accessible to all users (Thera or referral-only).
 */
export const ReferralRoutes = {
  Portal: '/referral',
  SignIn: '/referral/sign-in',
  Link: '/ref/[slug]',
  LinkBase: '/ref',
};

/**
 * Common routes for the application.
 */
export const CommonRoutes = {
  Dashboard: '/home',
  NotFound: '/404',
  // In-app Terms of Service page. Linked from the footer on native so the app
  // never opens getthera.com/terms (its ad-cookie banner is flagged by Apple as
  // tracking — App Store 5.1.2(i)). Web keeps linking to getthera.com.
  Terms: '/terms',
};
